
import MSCartesian from '@fusioncharts/charts/src/chart/_internal/mscartesian';
import HeatMapDataset from '../../dataset/heatmap';
import HeatMapGroup from '../../dataset/groups/heatmap';
import axisFactory from '../../factories/category-axis';
import dataSetFactory from '@fusioncharts/charts/src/factories/multiseries-dataset';
import createColorRangeManager from '@fusioncharts/charts/src/factories/colorrange';
import decideLegendCreation from '@fusioncharts/charts/src/factories/legendmanager';
import {
  chartPaletteStr,
  convertColor,
  hasSVG,
  pluck,
  preDefStr,
  pluckNumber,
  parseUnsafeString,
  getFirstValue,
  pluckFontSizeMaintainUnit
} from '@fusioncharts/core/src/lib';
import isEqualString from '@fusioncharts/utils/src/string/equal';

let defaultFontStr = preDefStr.defaultFontStr,
  divLineAlpha3DStr = preDefStr.divLineAlpha3DStr,
  divLineAlphaStr = preDefStr.divLineAlphaStr,
  altVGridColorStr = preDefStr.altVGridColorStr,
  altVGridAlphaStr = preDefStr.altVGridAlphaStr,
  altHGridColorStr = preDefStr.altHGridColorStr,
  altHGridAlphaStr = preDefStr.altHGridAlphaStr,
  colorStrings = preDefStr.colors,
  COLOR_000000 = colorStrings.c000000,
  EQUAL_STR = ' = ',
  HEATMAP = 'heatmap',
  UNDEF,
  checkValidId = item => (item.id !== UNDEF);

class HeatMap extends MSCartesian {
  /**
   * Provides the name of the chart extension
   *
   * @static
   * @return {string} The name of the chart extension
   */
  static getName () {
    return 'HeatMap';
  }

  constructor () {
    super();

    this.tooltipsepchar = EQUAL_STR;
    this.hasGradientLegend = true;

    this.registerFactory('axis', axisFactory, ['canvas']);
    // decide and create legend after creating canvas
    this.registerFactory('legend', decideLegendCreation, ['canvas']);

    // create color manager after creating legend-decider
    this.registerFactory('colormanager-decider', createColorRangeManager, ['legend']);

    // create dataset after creating colormanager-decider and vCanvas
    this.registerFactory('dataset', dataSetFactory, ['colormanager-decider', 'vCanvas']);
  }
  /**
   * Sets the name of the component
   * @return {string} name
   */
  getName () {
    return 'HeatMap';
  }

  __setDefaultConfig () {
    super.__setDefaultConfig();
    let config = this.config;
    config.isSingleSeries = true;
    config.hasLegend = true;
    config.defaultDatasetType = HEATMAP;
    config.enablemousetracking = true;
  }
  /**
   * function to check if the chart specific data is proper is not
   * this fn is define for specific chart types
   * @return {boolean} if JSON data is valid or not
   */
  _checkInvalidSpecificData () {
    let jsonData = this.getFromEnv('dataSource'),
      dataset = jsonData.dataset,
      data,
      colorrange = jsonData.colorrange;
    if (dataset) {
      for (let i = 0; i < dataset.length; i++) {
        if (dataset[i].data) {
          data = dataset[i].data;
          break;
        }
      }
    }
    if (!dataset || !data || data.length === 0 ||
       !colorrange || (!colorrange.color && !Number(colorrange.gradient))) {
      return true;
    }
  }

  /**
   * Parse the chart attributes and store in chart's config
   * @param {Object} dataObj User input json
   */
  parseChartAttr (dataObj) {
    super.parseChartAttr(dataObj);
    this.config.drawTrendRegion = 0;
  }
  configureAttributes (dataObj) {
    var iapi = this,
      chartConfig = iapi.config,
      dataSource = iapi.getFromEnv('dataSource'),
      skipConfigureIteration = iapi.config.skipConfigureIteration = {}, // eslint-disable-line good-practices/no-single-usage-variable
      rows,
      columns,
      datasetArray = dataObj.dataset,
      unifiedDataset = [];
    rows = dataSource.rows && dataSource.rows.row.filter(checkValidId);
    columns = dataSource.columns && dataSource.columns.column.filter(checkValidId);
    if (!rows || !columns) {
      // create unified dataset
      for (let i = 0; i < datasetArray.length; i++) {
        for (let j = 0; j < datasetArray[i].data.length; j++) {
          unifiedDataset.push(datasetArray[i].data[j]);
        }
      }
      if (!rows) {
        // get unique rows by rowid property
        rows = unifiedDataset.map(item => getFirstValue(item.rowid)).filter(
          (value, index, self) => self.indexOf(value) === index);
      }
      if (!columns) {
        // get unique columns by columnid property
        columns = unifiedDataset.map(item => getFirstValue(item.columnid)).filter(
          (value, index, self) => self.indexOf(value) === index);
      }
    }
    iapi.parseChartAttr(dataObj);

    iapi.addToEnv('chartColorRange', dataObj.colorrange || {});

    iapi.createComponent(dataObj);

    iapi._feedAxesRawData();
    skipConfigureIteration.axis = true;
    iapi.addToEnv('totalRows', rows.length);
    iapi.addToEnv('totalColumns', columns.length);

    iapi.configureChildren();
    // after configure chart calls its async draw
    iapi.asyncDraw();
    iapi.getFromEnv('toolTipController').setStyle({
      'backgroundColor': hasSVG ? convertColor((chartConfig.tooltipbgcolor || 'FFF'), (chartConfig.tooltipbgalpha || 100))
        : (chartConfig.tooltipbgcolor || 'FFF').replace(/\s+/g, '').replace(/^#?([a-f0-9]+)/ig, '#$1'),
      'color': (chartConfig.tooltipcolor || chartConfig.basefontcolor || '545454').replace(/^#?([a-f0-9]+)/ig, '#$1'),
      'borderColor': hasSVG ? convertColor((chartConfig.tooltipbordercolor || '666'), (chartConfig.tooltipborderalpha || 100))
        : (chartConfig.tooltipbordercolor || '666').replace(/\s+/g, '').replace(/^#?([a-f0-9]+)/ig, '#$1'),
      'borderWidth': pluckNumber(chartConfig.tooltipborderthickness, 1) + 'px',
      'showToolTipShadow': pluckNumber(chartConfig.showtooltipshadow || 0),
      'borderRadius': pluckNumber(chartConfig.tooltipborderradius, 0) + 'px',
      'fontSize': pluckNumber(chartConfig.basefontsize, 10) + 'px',
      'fontFamily': chartConfig.basefont || this.getFromEnv('style').inCanfontFamily,
      'padding': pluckNumber(chartConfig.tooltippadding || 3) + 'px'
    });
  }
  _postSpaceManagement () {
    var iapi = this,
      xAxis = iapi.getChildren('xAxis')[0],
      legend = iapi.getFromEnv('legend'),
      xDepth = iapi.config.xDepth; // eslint-disable-line good-practices/no-single-usage-variable
    iapi.setAxisDimention();
    xAxis && xAxis.shiftLabels(-xDepth, 0);
    legend && legend.postSpaceManager();

    iapi.allocateDimensionOfChartMenuBar();
  }

  /**
   * Sets dimension of Axis components
   */
  setAxisDimention () {
    let iapi = this,
      xAxis = iapi.getChildren('xAxis') && iapi.getChildren('xAxis')[0],
      yAxis = iapi.getChildren('yAxis') && iapi.getChildren('yAxis')[0],
      chartConfig = iapi.getFromEnv('chartConfig'),
      canvas = iapi.getChildren('canvas')[0],
      canvasConfig = canvas.config,
      {
        left,
        top,
        width,
        height,
        paddingLeft,
        paddingRight
      } = canvas.getEffectiveDimensions(),
      canvasBorderWidth = canvasConfig && canvasConfig.canvasBorderWidth,
      canvasPadding = canvasConfig && canvasConfig.canvasPadding;
    xAxis && xAxis.setAxisConfig({
      'canvasPaddingLeft': Math.max(paddingLeft, canvasPadding),
      'canvasPaddingRight': Math.max(paddingRight, canvasPadding)
    });
    xAxis && xAxis.setAxisDimention({
      x: left,
      y: xAxis.config.isOpposit ? (top + (chartConfig.shift || 0) - canvasBorderWidth)
        : (top + height + (chartConfig.shift || 0) + canvasBorderWidth),
      opposite: xAxis.config.isOpposit ? top + height + canvasBorderWidth
        : top - canvasBorderWidth,
      axisLength: width
    });
    yAxis && yAxis.setAxisDimention({
      x: yAxis.config.isOpposit
        ? left + width + canvasBorderWidth : left - canvasBorderWidth,
      y: top,
      opposite: yAxis.config.isOpposit
        ? left - canvasBorderWidth : left + width + canvasBorderWidth,
      axisLength: height
    });
  }
  _feedAxesRawData () {
    var iapi = this,
      colorM = iapi.getFromEnv('color-manager'),
      dataObj = iapi.getFromEnv('dataSource'),
      chartAttrs = dataObj.chart,
      xAxisConf,
      yAxisConf,
      is3D = iapi.config.is3D,
      palleteString = is3D ? chartPaletteStr.chart3D : chartPaletteStr.chart2D,
      xAxisPosition = parseUnsafeString(chartAttrs.xaxisposition).toLowerCase(),
      outCanfontSizeWithUnit = pluckFontSizeMaintainUnit(chartAttrs.outcnvbasefontsize, chartAttrs.basefontsize, 10);

    xAxisConf = {
      mapTickValuesById: true,
      isVertical: false,
      isReverse: false,
      isOpposit: pluckNumber(xAxisPosition === 'top' ? true : UNDEF,
        xAxisPosition === 'bottom' ? false : UNDEF, !!iapi.config.placeAxisLabelsOnTop),
      outCanfontFamily: pluck(chartAttrs.outcnvbasefont, chartAttrs.basefont, defaultFontStr),
      outCanfontSize: this.computeFontSize(outCanfontSizeWithUnit),
      outCanfontSizeWithUnit: outCanfontSizeWithUnit,
      outCancolor: pluck(chartAttrs.outcnvbasefontcolor, chartAttrs.basefontcolor,
        colorM.getColor(palleteString.baseFontColor)).replace(/^#?([a-f0-9]+)/ig, '#$1'),
      axisNamePadding: chartAttrs.xaxisnamepadding,
      axisValuePadding: pluckNumber(chartAttrs.labelxpadding, chartAttrs.labelpadding, 4),
      axisNameFont: chartAttrs.xaxisnamefont,
      axisNameFontSize: this.computeFontSize(chartAttrs.xaxisnamefontsize),
      axisNameFontSizeWithUnit: chartAttrs.xaxisnamefontsize,
      axisNameFontColor: chartAttrs.xaxisnamefontcolor,
      axisNameFontBold: chartAttrs.xaxisnamefontbold,
      axisNameFontItalic: chartAttrs.xaxisnamefontitalic,
      axisNameBgColor: chartAttrs.xaxisnamebgcolor,
      axisNameBorderColor: chartAttrs.xaxisnamebordercolor,
      axisNameAlpha: chartAttrs.xaxisnamealpha,
      axisNameFontAlpha: chartAttrs.xaxisnamefontalpha,
      axisNameBgAlpha: chartAttrs.xaxisnamebgalpha,
      axisNameBorderAlpha: chartAttrs.xaxisnameborderalpha,
      axisNameBorderPadding: chartAttrs.xaxisnameborderpadding,
      axisNameBorderRadius: chartAttrs.xaxisnameborderradius,
      axisNameBorderThickness: chartAttrs.xaxisnameborderthickness,
      axisNameBorderDashed: chartAttrs.xaxisnameborderdashed,
      axisNameBorderDashLen: chartAttrs.xaxisnameborderdashlen,
      axisNameBorderDashGap: chartAttrs.xaxisnameborderdashgap,
      useEllipsesWhenOverflow: chartAttrs.useellipseswhenoverflow,
      divLineColor: pluck(chartAttrs.vdivlinecolor, chartAttrs.divlinecolor,
        colorM.getColor(palleteString.divLineColor)),
      divLineAlpha: pluck(chartAttrs.vdivlinealpha, chartAttrs.divlinealpha,
        is3D ? colorM.getColor(divLineAlpha3DStr) : colorM.getColor(divLineAlphaStr)),
      divLineThickness: pluckNumber(chartAttrs.vdivlinethickness, chartAttrs.divlinethickness, 1),
      divLineIsDashed: Boolean(pluckNumber(chartAttrs.vdivlinedashed, chartAttrs.vdivlineisdashed,
        chartAttrs.divlinedashed, chartAttrs.divlineisdashed, 0)),
      divLineDashLen: pluckNumber(chartAttrs.vdivlinedashlen, chartAttrs.divlinedashlen, 4),
      divLineDashGap: pluckNumber(chartAttrs.vdivlinedashgap, chartAttrs.divlinedashgap, 2),
      showAlternateGridColor: pluckNumber(chartAttrs.showalternatevgridcolor, 0),
      alternateGridColor: pluck(chartAttrs.alternatevgridcolor, colorM.getColor(altVGridColorStr)),
      alternateGridAlpha: pluck(chartAttrs.alternatevgridalpha, colorM.getColor(altVGridAlphaStr)),
      numDivLines: chartAttrs.numvdivlines,
      labelFont: chartAttrs.labelfont,
      labelFontSize: this.computeFontSize(chartAttrs.labelfontsize),
      labelFontSizeWithUnit: chartAttrs.labelfontsize,
      labelFontColor: chartAttrs.labelfontcolor,
      labelAlpha: chartAttrs.labelalpha,
      labelFontBold: chartAttrs.labelfontbold,
      labelFontItalic: chartAttrs.labelfontitalic,
      labelBdrColor: chartAttrs.labelbordercolor,
      labelBdrAlpha: chartAttrs.labelborderalpha,
      labelLink: chartAttrs.labellink,
      labelBgColor: chartAttrs.labelbgcolor,
      labelBgAlpha: chartAttrs.labelbgalpha,
      labelBdrThickness: chartAttrs.labelborderthickness,
      labelBdrPadding: chartAttrs.labelborderpadding,
      labelBdrRadius: chartAttrs.labelborderradius,
      labelBdrDashed: chartAttrs.labelborderdashed,
      labelBdrDashLen: chartAttrs.labelborderdashlen,
      labelBdrDashGap: chartAttrs.labelborderdashgap,
      axisName: chartAttrs.xaxisname,
      axisMinValue: chartAttrs.xaxisminvalue,
      axisMaxValue: chartAttrs.xaxismaxvalue,
      setAdaptiveMin: chartAttrs.setadaptivexmin,
      adjustDiv: chartAttrs.adjustvdiv,
      labelDisplay: chartAttrs.labeldisplay,
      showLabels: pluckNumber(chartAttrs.showxaxislabels, chartAttrs.showlabels),
      rotateLabels: chartAttrs.rotatexaxislabels,
      slantLabel: pluckNumber(chartAttrs.slantlabels, chartAttrs.slantlabel),
      labelStep: pluckNumber(chartAttrs.labelstep, chartAttrs.xaxisvaluesstep),
      showAxisValues: pluckNumber(chartAttrs.showxaxisvalues, chartAttrs.showxaxisvalue),
      maxLabelHeight: chartAttrs.maxlabelheight,
      showZeroPlane: chartAttrs.showvzeroplane,
      zeroPlaneColor: chartAttrs.vzeroplanecolor,
      zeroPlaneThickness: chartAttrs.vzeroplanethickness,
      zeroPlaneAlpha: chartAttrs.vzeroplanealpha,
      showZeroPlaneValue: chartAttrs.showvzeroplanevalue,
      trendlineColor: chartAttrs.trendlinecolor,
      trendlineToolText: chartAttrs.trendlinetooltext,
      trendlineThickness: chartAttrs.trendlinethickness,
      trendlineAlpha: chartAttrs.trendlinealpha,
      showTrendlinesOnTop: chartAttrs.showtrendlinesontop,
      showAxisLine: pluckNumber(chartAttrs.showxaxisline, chartAttrs.showaxislines,
        chartAttrs.drawAxisLines, 0),
      axisLineThickness: pluckNumber(chartAttrs.xaxislinethickness, chartAttrs.axislinethickness, 1),
      axisLineAlpha: pluckNumber(chartAttrs.xaxislinealpha, chartAttrs.axislinealpha, 100),
      axisLineColor: pluck(chartAttrs.xaxislinecolor, chartAttrs.axislinecolor, COLOR_000000)
    };
    yAxisConf = {
      mapTickValuesById: true,
      isVertical: true,
      isReverse: false,
      isOpposit: isEqualString(chartAttrs.yaxisposition, 'right'),
      outCanfontFamily: pluck(chartAttrs.outcnvbasefont, chartAttrs.basefont, defaultFontStr),
      outCanfontSize: this.computeFontSize(outCanfontSizeWithUnit),
      outCanfontSizeWithUnit: outCanfontSizeWithUnit,
      outCancolor: pluck(chartAttrs.outcnvbasefontcolor, chartAttrs.basefontcolor,
        colorM.getColor(palleteString.baseFontColor)).replace(/^#?([a-f0-9]+)/ig, '#$1'),
      axisNamePadding: chartAttrs.yaxisnamepadding,
      axisValuePadding: pluckNumber(chartAttrs.labelypadding, chartAttrs.yaxisvaluespadding, 4),
      axisNameFont: chartAttrs.yaxisnamefont,
      axisNameFontSize: this.computeFontSize(chartAttrs.yaxisnamefontsize),
      axisNameFontSizeWithUnit: chartAttrs.yaxisnamefontsize,
      axisNameFontColor: chartAttrs.yaxisnamefontcolor,
      axisNameFontBold: chartAttrs.yaxisnamefontbold,
      axisNameFontItalic: chartAttrs.yaxisnamefontitalic,
      axisNameBgColor: chartAttrs.yaxisnamebgcolor,
      axisNameBorderColor: chartAttrs.yaxisnamebordercolor,
      axisNameAlpha: chartAttrs.yaxisnamealpha,
      axisNameFontAlpha: chartAttrs.yaxisnamefontalpha,
      axisNameBgAlpha: chartAttrs.yaxisnamebgalpha,
      axisNameBorderAlpha: chartAttrs.yaxisnameborderalpha,
      axisNameBorderPadding: chartAttrs.yaxisnameborderpadding,
      axisNameBorderRadius: chartAttrs.yaxisnameborderradius,
      axisNameBorderThickness: chartAttrs.yaxisnameborderthickness,
      axisNameBorderDashed: chartAttrs.yaxisnameborderdashed,
      axisNameBorderDashLen: chartAttrs.yaxisnameborderdashlen,
      axisNameBorderDashGap: chartAttrs.yaxisnameborderdashgap,
      axisNameWidth: chartAttrs.yaxisnamewidth,
      useEllipsesWhenOverflow: chartAttrs.useellipseswhenoverflow,
      rotateAxisName: pluckNumber(chartAttrs.rotateyaxisname, 1),
      axisName: chartAttrs.yaxisname,
      showAlternateGridColor: pluckNumber(chartAttrs.showalternatehgridcolor, 1),
      alternateGridColor: pluck(chartAttrs.alternatehgridcolor, colorM.getColor(altHGridColorStr)),
      alternateGridAlpha: pluck(chartAttrs.alternatehgridalpha, colorM.getColor(altHGridAlphaStr)),
      numDivLines: chartAttrs.numdivlines,
      axisMinValue: chartAttrs.yaxisminvalue,
      axisMaxValue: chartAttrs.yaxismaxvalue,
      setAdaptiveMin: chartAttrs.setadaptiveymin,
      adjustDiv: chartAttrs.adjustdiv,
      labelStep: chartAttrs.yaxisvaluesstep,
      showLabels: pluckNumber(chartAttrs.showyaxislabels, chartAttrs.showlabels),
      maxLabelWidthPercent: chartAttrs.maxlabelwidthpercent,
      showAxisValues: pluckNumber(chartAttrs.showyaxisvalues, chartAttrs.showyaxisvalue),
      divLineColor: pluck(chartAttrs.hdivlinecolor, colorM.getColor(palleteString.divLineColor)),
      divLineAlpha: pluck(chartAttrs.hdivlinealpha, colorM.getColor(divLineAlphaStr)),
      divLineThickness: pluckNumber(chartAttrs.hdivlinethickness, 1),
      divLineIsDashed: Boolean(pluckNumber(chartAttrs.hdivlinedashed, chartAttrs.hdivlineisdashed, 0)),
      divLineDashLen: pluckNumber(chartAttrs.hdivlinedashlen, 4),
      divLineDashGap: pluckNumber(chartAttrs.hdivlinedashgap, 2),
      // showLimits: chartAttrs.showlimits,
      // showDivLineValues: pluckNumber(chartAttrs.showdivlinevalues, chartAttrs.showdivlinevalue),
      showZeroPlane: chartAttrs.showzeroplane,
      zeroPlaneColor: chartAttrs.zeroplanecolor,
      zeroPlaneThickness: chartAttrs.zeroplanethickness,
      zeroPlaneAlpha: chartAttrs.zeroplanealpha,
      showZeroPlaneValue: chartAttrs.showzeroplanevalue,
      trendlineColor: chartAttrs.trendlinecolor,
      trendlineToolText: chartAttrs.trendlinetooltext,
      trendlineThickness: chartAttrs.trendlinethickness,
      trendlineAlpha: chartAttrs.trendlinealpha,
      showTrendlinesOnTop: chartAttrs.showtrendlinesontop,
      showAxisLine: pluckNumber(chartAttrs.showyaxisline, chartAttrs.showaxislines,
        chartAttrs.drawAxisLines, 0),
      axisLineThickness: pluckNumber(chartAttrs.yaxislinethickness, chartAttrs.axislinethickness, 1),
      axisLineAlpha: pluckNumber(chartAttrs.yaxislinealpha, chartAttrs.axislinealpha, 100),
      axisLineColor: pluck(chartAttrs.yaxislinecolor, chartAttrs.axislinecolor, COLOR_000000),
      labelFont: chartAttrs.labelfont,
      labelFontSize: this.computeFontSize(chartAttrs.labelfontsize),
      labelFontSizeWithUnit: chartAttrs.labelfontsize,
      labelFontColor: chartAttrs.labelfontcolor,
      labelAlpha: chartAttrs.labelalpha,
      labelFontBold: chartAttrs.labelfontbold,
      labelFontItalic: chartAttrs.labelfontitalic,
      labelBdrColor: chartAttrs.labelbordercolor,
      labelBdrAlpha: chartAttrs.labelborderalpha,
      labelLink: chartAttrs.labellink,
      labelBgColor: chartAttrs.labelbgcolor,
      labelBgAlpha: chartAttrs.labelbgalpha,
      labelBdrThickness: chartAttrs.labelborderthickness,
      labelBdrPadding: chartAttrs.labelborderpadding,
      labelBdrRadius: chartAttrs.labelborderradius,
      labelBdrDashed: chartAttrs.labelborderdashed,
      labelBdrDashLen: chartAttrs.labelborderdashlen,
      labelBdrDashGap: chartAttrs.labelborderdashgap
    };
    xAxisConf.vtrendlines = dataObj.vtrendlines;
    yAxisConf.trendlines = dataObj.trendlines;
    return {
      yAxisConf: [yAxisConf],
      xAxisConf: [xAxisConf]
    };
  }

  _setCategories () {
    var iapi = this,
      dataObj = iapi.getFromEnv('dataSource'),
      xAxis = iapi.getChildren('xAxis'),
      yAxis = iapi.getChildren('yAxis'),
      len,
      i,
      k,
      columnObj,
      rowObj,
      columns,
      rows,
      columnid,
      rowid,
      columnArr = [],
      rowArr = [],
      columnFlag,
      rowFlag,
      j;

    if (!dataObj.columns || !dataObj.rows) {
      dataObj.columns = {};
      dataObj.columns.column = columns = [];

      dataObj.rows = {};
      dataObj.rows.row = rows = [];

      for (k = 0; k < (dataObj.dataset && dataObj.dataset.length); k++) {
        len = dataObj.dataset && dataObj.dataset[k].data && dataObj.dataset[k].data.length;
        for (i = 0; i < len; i++) {
          columnid = getFirstValue(dataObj.dataset[k].data[i].columnid);
          rowid = getFirstValue(dataObj.dataset[k].data[i].rowid);
          columnFlag = true;
          rowFlag = true;

          for (j = 0; j < columns.length; j++) {
            if (columnid === columns[j].id) {
              columnFlag = false;
            }
          }
          if (columnFlag) {
            columnObj = {
              id: columnid,
              label: columnid
            };
            dataObj.columns.column.push(columnObj);
          }

          for (j = 0; j < rows.length; j++) {
            if (rowid === rows[j].id) {
              rowFlag = false;
            }
          }
          if (rowFlag) {
            rowObj = {
              id: rowid,
              label: rowid
            };
            dataObj.rows.row.push(rowObj);
          }
        }
      }
    }

    columns = dataObj.columns.column;
    rows = dataObj.rows.row;

    for (i = 0; i < (columns && columns.length); i++) {
      columns[i].label = pluck(columns[i].label, columns[i].name, columns[i].id);
      columnArr.push(columns[i]);
    }
    dataObj.columns.column = columnArr;

    for (i = 0; i < (rows && rows.length); i++) {
      rows[i].label = pluck(rows[i].label, rows[i].name, rows[i].id);
      rowArr.push(rows[i]);
    }
    dataObj.rows.row = rowArr;

    // dataObj.columns && xAxis[0].setAxisPadding(0.5, 0.5);
    dataObj.columns && xAxis[0].setTickValues(dataObj.columns.column);

    // dataObj.columns && yAxis[0].setAxisPadding(0.5, 0.5);
    dataObj.rows && yAxis[0].setTickValues(dataObj.rows.row);

    xAxis[0].setAxisConfig({
      categoryNumDivLines: iapi.getFromEnv('totalColumns') - 1,
      categoryDivLinesFromZero: 0,
      showAlternateGridColor: 0
    });

    yAxis[0].setAxisConfig({
      categoryNumDivLines: iapi.getFromEnv('totalRows') - 1,
      categoryDivLinesFromZero: 0,
      showAlternateGridColor: 0
    });
  }
  getDSdef () {
    return HeatMapDataset;
  }

  getDSGroupdef () {
    return HeatMapGroup;
  }
}
export default HeatMap;
